import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import apiClient from '@/util/apiClient';
import posthog from '@/composables/usePostHog.js';

const RANK_SCOPE = Object.freeze({
	GROUP: 'Group'
} as const);

type RankScope = (typeof RANK_SCOPE)[keyof typeof RANK_SCOPE];

interface GroupRankingRef {
	groupNumber?: number | string | null;
	name?: string | null;
	shortName?: string | null;
}

interface UserRankingRef {
	userNumber?: number | string | null;
	firstName?: string | null;
	middleName?: string | null;
	lastName?: string | null;
	photo?: string | null;
}

interface OfficialRanking {
	rankingsNumber?: number | string | null;
	rankScope?: RankScope | null;
	group?: GroupRankingRef | null;
	groupNumber?: number | string | null;
	groupId?: number | string | null;
	client?: unknown;
	user?: UserRankingRef | null;
	userNumber?: number | string | null;
	userId?: number | string | null;
	crRank?: number | string | null;
	arRank?: number | string | null;
}

interface RankingPatchPayload {
	crRank?: number | string | null;
	arRank?: number | string | null;
}

interface NormalizedRankingPatchPayload {
	crRank?: number | null;
	arRank?: number | null;
}

interface SaveGroupRankingPayload {
	existingRanking?: OfficialRanking | null;
	groupId: number | string;
	userId: number | string;
	crRank?: number | string | null;
	arRank?: number | string | null;
}

interface OfficialRankingState {
	myRankings: OfficialRanking[];
	groupRankings: OfficialRanking[];
	groupRankingsByGroup: Record<string, OfficialRanking[]>;
	loadedMyRankings: boolean;
	loadedGroups: Record<string, boolean>;
}

function toArray(data: unknown): OfficialRanking[] {
	return Array.isArray(data) ? (data as OfficialRanking[]) : [];
}

function toRankValue(value: unknown): number | null {
	if (value === '' || value == null) return null;

	const n = Number(value);
	return Number.isFinite(n) ? n : null;
}

function toFiniteNumber(value: unknown): number | null {
	const n = Number(value);
	return Number.isFinite(n) ? n : null;
}

function getGroupNumber(ranking: OfficialRanking | null | undefined): number {
	return Number(ranking?.group?.groupNumber ?? ranking?.groupNumber ?? ranking?.groupId);
}

function getUserNumber(ranking: OfficialRanking | null | undefined): number {
	return Number(ranking?.user?.userNumber ?? ranking?.userNumber ?? ranking?.userId);
}

function normalizeSavePayload(payload: OfficialRanking): OfficialRanking {
	return {
		...payload,
		rankScope: RANK_SCOPE.GROUP,
		client: null,
		crRank: toRankValue(payload.crRank),
		arRank: toRankValue(payload.arRank)
	};
}

function normalizePatchPayload(payload: RankingPatchPayload): NormalizedRankingPatchPayload {
	const patch: NormalizedRankingPatchPayload = {};

	if (Object.prototype.hasOwnProperty.call(payload, 'crRank')) {
		patch.crRank = toRankValue(payload.crRank);
	}

	if (Object.prototype.hasOwnProperty.call(payload, 'arRank')) {
		patch.arRank = toRankValue(payload.arRank);
	}

	return patch;
}

export const useOfficialRankingStore = defineStore('officialRankingStore', {
	state: (): OfficialRankingState => ({
		myRankings: [],
		groupRankings: [],
		groupRankingsByGroup: {},
		loadedMyRankings: false,
		loadedGroups: {}
	}),

	getters: {
		getRankingsForGroup: (state) => {
			return (groupNumber: number | string): OfficialRanking[] => {
				return state.groupRankingsByGroup[String(groupNumber)] ?? [];
			};
		},

		getRankingsForUserInGroup: (state) => {
			return (groupNumber: number | string, userNumber: number | string): OfficialRanking[] => {
				const rankings = state.groupRankingsByGroup[String(groupNumber)] ?? [];

				return rankings.filter((ranking) => {
					return getUserNumber(ranking) === Number(userNumber);
				});
			};
		},

		getGroupRankingForUser: (state) => {
			return (groupNumber: number | string, userNumber: number | string): OfficialRanking | undefined => {
				const rankings = state.groupRankingsByGroup[String(groupNumber)] ?? [];

				return rankings.find((ranking) => {
					return ranking.rankScope === RANK_SCOPE.GROUP && getUserNumber(ranking) === Number(userNumber);
				});
			};
		}
	},

	actions: {
		upsertRankings(rankings: unknown): void {
			toArray(rankings).forEach((ranking) => {
				const groupNumber = getGroupNumber(ranking);
				const userNumber = getUserNumber(ranking);
				const rankScope = ranking.rankScope;

				if (!Number.isFinite(groupNumber) || !Number.isFinite(userNumber) || rankScope !== RANK_SCOPE.GROUP) {
					return;
				}

				const key = String(groupNumber);
				const existing = this.groupRankingsByGroup[key] ?? [];

				const next = existing.filter((item) => {
					const itemRankingNumber = toFiniteNumber(item.rankingsNumber);
					const incomingRankingNumber = toFiniteNumber(ranking.rankingsNumber);

					const sameRankingNumber = itemRankingNumber != null && incomingRankingNumber != null && itemRankingNumber === incomingRankingNumber;

					if (sameRankingNumber) {
						return false;
					}

					const sameGroup = getGroupNumber(item) === groupNumber;
					const sameUser = getUserNumber(item) === userNumber;
					const sameScope = item.rankScope === RANK_SCOPE.GROUP;

					return !(sameGroup && sameUser && sameScope);
				});

				this.groupRankingsByGroup[key] = [...next, ranking];

				const currentGroupNumber = this.groupRankings[0] ? getGroupNumber(this.groupRankings[0]) : null;

				if (String(currentGroupNumber ?? '') === key || this.groupRankings.length === 0) {
					this.groupRankings = this.groupRankingsByGroup[key] ?? [];
				}
			});
		},

		removeRankingFromState(rankNumber: number | string): void {
			const normalizedRankNumber = Number(rankNumber);

			Object.keys(this.groupRankingsByGroup).forEach((key) => {
				this.groupRankingsByGroup[key] = (this.groupRankingsByGroup[key] ?? []).filter((ranking) => {
					return Number(ranking.rankingsNumber) !== normalizedRankNumber;
				});
			});

			this.groupRankings = this.groupRankings.filter((ranking) => {
				return Number(ranking.rankingsNumber) !== normalizedRankNumber;
			});

			this.myRankings = this.myRankings.filter((ranking) => {
				return Number(ranking.rankingsNumber) !== normalizedRankNumber;
			});
		},

		clearGroupRankings(groupNumber: number | string): void {
			const key = String(groupNumber);

			this.groupRankingsByGroup[key] = [];
			this.loadedGroups[key] = false;

			if (this.groupRankings.length && String(getGroupNumber(this.groupRankings[0])) === key) {
				this.groupRankings = [];
			}
		},

		async fetchAllRankings(): Promise<OfficialRanking[]> {
			const response = await apiClient.get<unknown>('/official/rankings');
			return toArray(response.data);
		},

		async fetchRankingByRankNumber(rankNumber: number | string): Promise<OfficialRanking> {
			const response = await apiClient.get<OfficialRanking>(`/official/rankings/${rankNumber}`);
			return response.data;
		},

		async fetchMyRankings(force = false): Promise<OfficialRanking[]> {
			if (shouldThrottle('fetchMyRankings', 100)) {
				posthog.capture('throttled_action', { action: 'fetchMyRankings' });
				return this.myRankings;
			}

			if (this.loadedMyRankings && !force) {
				return this.myRankings;
			}

			const response = await apiClient.get<unknown>('/official/rankings/my-rankings');
			const rankings = toArray(response.data).filter((ranking) => ranking.rankScope === RANK_SCOPE.GROUP);

			this.myRankings = rankings;
			localStorage.setItem('user_rankings', JSON.stringify(rankings));
			this.loadedMyRankings = true;

			return this.myRankings;
		},

		async fetchGroupRankings(groupNumber: number | string, force = false): Promise<OfficialRanking[]> {
			const key = String(groupNumber);

			if (!force && this.loadedGroups[key]) {
				return this.groupRankingsByGroup[key] ?? [];
			}

			const response = await apiClient.get<unknown>(`/official/rankings/group/${groupNumber}`);
			const rankings = toArray(response.data).filter((ranking) => ranking.rankScope === RANK_SCOPE.GROUP);

			this.groupRankingsByGroup[key] = rankings;
			this.groupRankings = rankings;
			this.loadedGroups[key] = true;

			return rankings;
		},

		async fetchUserRankingsForGroup(groupNumber: number | string, userNumber: number | string, force = false): Promise<OfficialRanking[]> {
			const rankings = await this.fetchGroupRankings(groupNumber, force);

			return rankings.filter((ranking) => {
				return getUserNumber(ranking) === Number(userNumber);
			});
		},

		async fetchGroupRankingForUser(groupNumber: number | string, userNumber: number | string, force = false): Promise<OfficialRanking | undefined> {
			const rankings = await this.fetchUserRankingsForGroup(groupNumber, userNumber, force);

			return rankings.find((ranking) => {
				return ranking.rankScope === RANK_SCOPE.GROUP;
			});
		},

		async addRanking(ranking: OfficialRanking | OfficialRanking[]): Promise<OfficialRanking | OfficialRanking[] | undefined> {
			if (shouldThrottle('addRanking', 100)) {
				posthog.capture('throttled_action', { action: 'addRanking' });
				return undefined;
			}

			const payloads = Array.isArray(ranking) ? ranking : [ranking];

			const responses = await Promise.all(
				payloads.map((payload) => {
					return apiClient.post<OfficialRanking | null>('/official/rankings/save', normalizeSavePayload(payload));
				})
			);

			const savedRankings = responses.map((response) => response.data).filter((savedRanking): savedRanking is OfficialRanking => Boolean(savedRanking));

			this.upsertRankings(savedRankings);

			return Array.isArray(ranking) ? savedRankings : savedRankings[0];
		},

		async patchGroupRanking(groupNumber: number | string, userNumber: number | string, mergePatch: RankingPatchPayload): Promise<OfficialRanking | null> {
			const patch = normalizePatchPayload(mergePatch);

			if (Object.keys(patch).length === 0) {
				return null;
			}

			const response = await apiClient.patch<OfficialRanking | null>(`/official/rankings/edit/groups/${groupNumber}/users/${userNumber}`, patch, {
				headers: {
					'Content-Type': 'application/merge-patch+json'
				}
			});

			if (!response.data) {
				return null;
			}

			const ranking = response.data;
			this.upsertRankings([ranking]);

			return ranking;
		},

		async saveGroupRanking({ existingRanking, groupId, userId, crRank, arRank }: SaveGroupRankingPayload): Promise<OfficialRanking | OfficialRanking[] | null | undefined> {
			const normalizedGroupId = toFiniteNumber(groupId);
			const normalizedUserId = toFiniteNumber(userId);

			if (normalizedGroupId == null || normalizedUserId == null) {
				throw new Error('groupId and userId are required');
			}

			const normalizedCrRank = toRankValue(crRank);
			const normalizedArRank = toRankValue(arRank);

			if (existingRanking?.rankingsNumber) {
				return this.patchGroupRanking(normalizedGroupId, normalizedUserId, {
					crRank: normalizedCrRank,
					arRank: normalizedArRank
				});
			}

			if (normalizedCrRank == null && normalizedArRank == null) {
				return null;
			}

			return this.addRanking({
				rankScope: RANK_SCOPE.GROUP,
				groupId: normalizedGroupId,
				client: null,
				userId: normalizedUserId,
				crRank: normalizedCrRank,
				arRank: normalizedArRank
			});
		},

		async deleteRanking(rankNumber: number | string): Promise<void> {
			await apiClient.delete(`/official/rankings/delete/${rankNumber}`);
			this.removeRankingFromState(rankNumber);
		}
	}
});
